import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  catchError,
  firstValueFrom,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { AIListBuilderViewModel } from '../model/ai-list-builder-view.model';
import {
  selectAIListBuilderViewModel,
  alternativeIngredientsWithCurrentFirstSelector,
  alternativeIngredientsWithSelectionInfoSelector,
} from '../selectors/ai-list-view-model.selector';
import { UserPhoto } from '@shared/services/photo/model/photo.model';
import { AIListS3Response } from '@shared/components/camera-uploader-square/ai-list-save-response.interface';
import {
  FILE_ERRORS,
  AILIST_FILE_EXTENSIONS,
  TEN_MB_LIMIT,
  UserFile,
} from '@shared/services/file/model/file.model';
import { PhotoService } from '@shared/services/photo/photo.service';
import { HttpClient } from '@angular/common/http';
import { FileService } from '@shared/services/file/file.service';
import { ActionSheetController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { Button } from '@shared/models/ellipsis-option.model';
import {
  AiListMetaDataService,
  AI_LIST_ACTIONS,
  IAIListSearchResultsState,
} from '@usf/ngrx-list-ai';
import { IAIListFilterState, selectSearchResults } from '@usf/ngrx-list-ai';
import { SearchRequest } from '@usf/list-ai-types';
import { ProductService } from '@shared/services/product/product.service';
import { LoadingState } from '@usf/ngrx-product';
import { Product } from '@shared/models/product.model';

@Injectable({
  providedIn: 'root',
})
export class AIListBuilderService {
  aiListBuilderViewModel$: Observable<AIListBuilderViewModel> =
    this.store.select(selectAIListBuilderViewModel);
  private aiListPhotosSubject = new BehaviorSubject<UserPhoto[]>([]);
  private _aiListPhotos$: Observable<UserPhoto[]> =
    this.aiListPhotosSubject.asObservable();

  private aiListPhotosErrorSubject = new BehaviorSubject<string | null>(null);
  isIngredientMenuOpen$ = new BehaviorSubject<boolean>(false);
  aiListPhotosError$ = this.aiListPhotosErrorSubject.asObservable();

  // Creating a specific subject for the current ingredient being viewed/edited
  private currentIngredientSubject = new BehaviorSubject<Product | null>(null);
  currentIngredient$ = this.currentIngredientSubject.asObservable();

  // Subject for tracking the selected product in the ingredient menu
  private selectedProductSubject = new BehaviorSubject<Product | null>(null);

  // Observable that handles auto-selection logic
  selectedProduct$: Observable<Product | null> =
    this.getAlternativeIngredientsWithSelectionInfo().pipe(
      switchMap(({ ingredients, matchingProductNumber }) => {
        // If there's a matching product and no product is currently selected
        if (matchingProductNumber && ingredients.length > 0) {
          const matchingProduct = ingredients.find(
            ingredient => ingredient.productNumber === matchingProductNumber,
          );

          // Auto-select the matching product if found and nothing is selected
          if (matchingProduct && !this.selectedProductSubject.getValue()) {
            this.selectedProductSubject.next(matchingProduct);
          }
        }

        // Return the current selection
        return this.selectedProductSubject.asObservable();
      }),
    );

  constructor(
    private readonly store: Store,
    private photoService: PhotoService,
    private http: HttpClient,
    private fileService: FileService,
    private platform: Platform,
    private actionSheetCtrl: ActionSheetController,
    private translateService: TranslateService,
    private aiListMetadataService: AiListMetaDataService,
    private productService: ProductService,
  ) {}

  public get aiListPhotos$(): Observable<UserPhoto[]> {
    return this._aiListPhotos$;
  }

  getAiListPhotos() {
    return this.aiListPhotosSubject.value;
  }

  // Label a photo as done loading
  updateAiListPhoto(s3Response: AIListS3Response) {
    let photos = [];
    if (this.aiListPhotosSubject.value) {
      photos = Array.from(this.aiListPhotosSubject.value);
    }
    photos.find((photo: UserPhoto) => {
      if (photo.filepath == s3Response.filename) {
        photo.filepath = s3Response.path;
      }
      photo.doneLoading = true;
    });
    this.aiListPhotosSubject.next(photos);
  }

  // Send AI List photos to s3
  async sendFilesToS3(userFile: UserFile, blob: Blob) {
    const formData: FormData = new FormData();
    formData.append('file', blob, userFile.name);

    let updatedArray = [
      ...this.aiListPhotosSubject.value,
      ...([
        {
          filepath: userFile.filepath,
          webviewPath: userFile.webviewPath, //path,
          extension: userFile.extension,
          doneLoading: false,
        } as UserPhoto,
      ] as UserPhoto[]),
    ];
    this.aiListPhotosSubject.next(updatedArray);

    await firstValueFrom(
      this.http.post(environment.listAIApiUrl + '/uploadFile', formData).pipe(
        map((res: AIListS3Response) => {
          this.updateAiListPhoto(res);
          this.aiListPhotosErrorSubject.next('');
        }),
        catchError((err: any) => {
          let updatedArrayRemovedFailed = this.aiListPhotosSubject.value;
          updatedArrayRemovedFailed.pop();
          this.aiListPhotosSubject.next([...updatedArrayRemovedFailed]);
          this.aiListPhotosErrorSubject.next(
            this.translateService.instant(
              'i18n.lists.aiListBuilderModal.errorMessageFailedUpload',
            ),
          );
          return of(err);
        }),
      ),
    );
  }

  private async getFileFromFilePickerPlugin() {
    try {
      const pickedFile = await this.fileService.getPickedFileFromFilePicker(
        AILIST_FILE_EXTENSIONS,
        await this.calculateMaxFileSize(true),
      );
      let dataToSend;
      if (this.platform.is('hybrid') || this.platform.is('mobile')) {
        const data = await this.fileService.pickedFileToBlob(pickedFile);
        dataToSend = data;
      } else {
        dataToSend = pickedFile.blob;
      }
      const userFile = this.fileService.getUserFileFromPickedFile(pickedFile);
      this.sendFilesToS3(userFile, dataToSend);
    } catch (err) {
      if (err.message === FILE_ERRORS.wrongSize) {
        this.aiListPhotosErrorSubject.next(
          this.translateService.instant(
            'i18n.lists.aiListBuilderModal.errorMessageFileSizeExceeded',
            {
              size: await this.calculateMaxFileSize(false),
            },
          ),
        );
      }
      if (err.message === FILE_ERRORS.wrongMime) {
        this.aiListPhotosErrorSubject.next(
          this.translateService.instant(
            'i18n.lists.aiListBuilderModal.errorMessageFailedUpload',
          ),
        );
      }
    }
  }

  private async getFileFromCameraPlugin() {
    const pickedPhoto = await this.photoService.getJPEGPhotoFromPhotoLibrary(
      await this.calculateMaxFileSize(true),
    );
    const userFile = this.photoService.getUserFileFromPhoto(pickedPhoto);
    const file = await this.photoService.photoToBlob(
      pickedPhoto,
      userFile.name,
    );
    this.sendFilesToS3(userFile, file);
  }

  onDeletePhoto(index: number) {
    this.aiListPhotosSubject.next(
      Array.from(this.aiListPhotosSubject.value).filter((_, i) => i !== index),
    );
  }

  clearAiListPhotosAndErrors() {
    this.aiListPhotosSubject.next([]);
    this.aiListPhotosErrorSubject.next('');
  }

  // Add photo to list
  async onAddAIListPhoto() {
    try {
      if (this.platform.is('hybrid') || this.platform.is('mobile')) {
        const buttons: Button[] = [
          {
            // From camera plugin
            text: this.translateService.instant('i18n.common.photoLibrary'),
            handler: () => {
              this.getFileFromCameraPlugin();
            },
          },
          {
            // From files
            text: this.translateService.instant('i18n.common.chooseFiles'),
            handler: () => {
              this.getFileFromFilePickerPlugin();
            },
          },
        ];
        buttons.push({
          text: this.translateService.instant('i18n.common.cancel'),
          role: 'cancel',
          cssClass: 'cancel-button',
        });
        const actionSheet = await this.actionSheetCtrl.create({
          cssClass: 'action-sheet-touch',
          mode: 'ios',
          buttons,
        });
        await actionSheet.present();
      } else {
        // Desktop
        this.getFileFromFilePickerPlugin();
      }
    } catch (error) {
      // TODO: Log and/or handle error client side
      // Error is either from size limit or picker was canceled or wrong mime type, handle accordingly
    }
  }

  private async calculateMaxFileSize(isMegabytes: boolean) {
    const maxFileSize = await firstValueFrom(
      this.aiListMetadataService.maxFileSize$,
    );
    if (isMegabytes) {
      return 1024 * 1024 * maxFileSize;
    } else {
      return maxFileSize;
    }
  }

  getAlternativeIngredientsWithCurrentFirst(): Observable<Product[]> {
    return this.store.select(alternativeIngredientsWithCurrentFirstSelector);
  }

  getAlternativeIngredientsWithSelectionInfo(): Observable<{
    ingredients: Product[];
    matchingProductNumber: number | null;
  }> {
    return this.store
      .select(alternativeIngredientsWithSelectionInfoSelector)
      .pipe(
        // Combine with the current ingredient to find matches
        switchMap(data => {
          return this.currentIngredient$.pipe(
            map(currentIngredient => {
              // If we have a current ingredient and ingredients list
              if (currentIngredient && data.ingredients.length > 0) {
                // Find if the current ingredient matches any in the list
                const matchingProductNumber = data.ingredients.some(
                  ingredient =>
                    ingredient.productNumber ===
                    currentIngredient.productNumber,
                )
                  ? currentIngredient.productNumber
                  : null;

                return {
                  ingredients: data.ingredients,
                  matchingProductNumber,
                };
              }
              return data;
            }),
          );
        }),
      );
  }

  setCurrentIngredient(product: Product): void {
    this.currentIngredientSubject.next(product);
  }

  clearCurrentIngredient(): void {
    this.currentIngredientSubject.next(null);
  }

  setSelectedProduct(product: Product | null): void {
    this.selectedProductSubject.next(product);
  }

  clearSelectedProduct(): void {
    this.selectedProductSubject.next(null);
  }

  clearProductSearchRestults(): void {
    this.store.dispatch(AI_LIST_ACTIONS.resetSearchResultState());
  }

  getSelectedProduct(): Product | null {
    return this.selectedProductSubject.getValue();
  }

  searchAltIngredient(searchRequest: SearchRequest): void {
    if (!searchRequest) {
      return;
    }

    this.store.dispatch(AI_LIST_ACTIONS.search({ request: searchRequest }));
  }

  selectAlternativeIngredientsLoading(): Observable<LoadingState> {
    return this.store.select(selectSearchResults).pipe(
      switchMap(searchResultsState => {
        const productNumbers =
          searchResultsState?.searchResults?.products?.map(product =>
            Number(product.productNumber),
          ) || [];

        return this.productService.selectLoadingStateOfProducts(productNumbers);
      }),
    );
  }

  selectAogSearchResultsLoading(): Observable<IAIListSearchResultsState> {
    return this.store.select(selectSearchResults);
  }

  searchInList(searchKey: string) {
    this.store.dispatch(AI_LIST_ACTIONS.searchInList({ searchKey }));
  }

  resetSearch() {
    this.store.dispatch(AI_LIST_ACTIONS.searchInList({ searchKey: '' }));
  }

  updateFilterOptions(filterOptions: IAIListFilterState): void {
    this.store.dispatch(AI_LIST_ACTIONS.updateFilterOptions({ filterOptions }));
  }

  updateMenuFilterOptions(filters: string[]): void {
    this.store.dispatch(AI_LIST_ACTIONS.updateMenuFilterOptions({ filters }));
  }

  searchInIngredientMenu(searchKey: string): void {
    this.store.dispatch(AI_LIST_ACTIONS.searchMenuResults({ searchKey }));
  }

  resetIngredientMenuSearch(): void {
    this.store.dispatch(AI_LIST_ACTIONS.searchMenuResults({ searchKey: '' }));
  }
}
